import { createColumnHelper } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle } from "@fortawesome/free-solid-svg-icons";
import { LogLevel, LogLevelStyles, Permissions } from "../../app/enums";
import { ITopMessage } from "../../models/TopMessages";
import { usePermissionCheck } from "../../app/common/helper/Permissions";
import { NavLink } from "react-router-dom";
import SimpleTable from "../../components/simpletable";

interface ITopMessagesTableRow extends ITopMessage {
  searchableText: string;
}

export interface IPendingMessagesProps {
  data: ITopMessage[];
  isLoading: boolean;
}

export default function PendingMessages(props: IPendingMessagesProps) {
  const columnHelper = createColumnHelper<ITopMessagesTableRow>();

  let CanViewEstablishments: boolean = usePermissionCheck(Permissions.CanViewEstablishments);

    const formatDaysSeen = (days: number): string => {
    if (days < 1) {
      const hours = Math.round(days * 24);
      return hours === 1 ? '1 hour' : `${hours} hours`;
    } else if (days < 7) {
      const roundedDays = Math.round(days);
      return roundedDays === 1 ? '1 day' : `${roundedDays} days`;
    } else {
      const weeks = Math.round(days / 7);
      return weeks === 1 ? '1 week' : `${weeks} weeks`;
    }
  };

  const columns = [
    columnHelper.display({
      id: 'rowNumber',  
      header: () => <span style={{ width: '15px', display: 'block' }}>#</span>,
      size: 1,
      minSize: 1,
      maxSize: 1,
      cell: ({ row }) => (
        <div className="short-text" style={{ width: '15px' }}>
          <span>{row.index + 1}</span>
        </div>
      ),
    }),
    columnHelper.accessor('establishmentName', {
      header: () => <span>Establishment</span>,
      size: 15,
      minSize: 15,
      maxSize: 15,
      cell: ({ row }) => (
        <div className="long-text" title={row.original.establishmentName as string}>
          {
            CanViewEstablishments ?
              <NavLink to={`/establishment/${row.original.establishmentID}`}>
                {row.original.establishmentName}
              </NavLink>
              :
              row.original.establishmentName
          }
        </div>
      ),
    }),
    columnHelper.accessor('message', {
      header: () => <span>Message</span>,
      size: 20,
      minSize: 20,
      maxSize: 20,
      cell: ({ row }) => (
        <div className="long-text" title={row.original.message as string}>
          <span>{row.original.message}</span>
        </div>
      ),
    }),
    columnHelper.accessor('daysSeen', {
      header: () => <span>Last Seen</span>,
      size: 12,
      minSize: 12,
      maxSize: 12,
      cell: ({ row }) => (
        <div className="short-text">
          <span>{row.original.daysSeen > 0 ? formatDaysSeen(row.original.daysSeen)+ ' ago' : ''}</span>
        </div>
      ),
    }),
    columnHelper.accessor('instanceCount', {
      header: () => <span>Instance Count</span>,
      size: 12,
      minSize: 12,
      maxSize: 12,
      cell: ({ row }) => (
        <div className="short-text">
          <span>{row.original.instanceCount}</span>
        </div>
      ),
    }),
    columnHelper.accessor('deviceName', {
      header: () => <span>Device</span>,
      size: 12,
      minSize: 12,
      maxSize: 12,
      cell: ({ row }) => (
        <div className="short-text" title={row.original.deviceName as string}>
          <span>{row.original.deviceName}</span>
        </div>
      ),
    }),
    columnHelper.accessor('productName', {
      header: () => <span>Product</span>,
      size: 12,
      minSize: 12,
      maxSize: 12,
      cell: ({ row }) => (
        <div className="short-text" title={row.original.productName as string}>
          <span>{row.original.productName}</span>
        </div>
      ),
    }),
    columnHelper.accessor('logLevel', {
      header: () => <span>Log Level</span>,
      size: 12,
      minSize: 12,
      maxSize: 12,
      cell: ({ row }) => (
        <div className="loglevel-box">
          <div className={`p-1 ps-2 pe-2 logs ${LogLevelStyles[row.original.logLevel]}`}>
            <FontAwesomeIcon icon={faCircle} size="2xs"
              className={`${LogLevelStyles[row.original.logLevel]}`} />
            <span className="ms-2">{LogLevel[row.original.logLevel]}</span>
          </div>
        </div>
      ),
    }),
  ];

  return (
    <div className="top-messages-table">
      <SimpleTable
        tableKey={"pending-messages-table"}
        columns={columns}
        data={props.data}
        isLoading={props.isLoading}
      />
    </div>
  );
}
